import appStore from '../store';
import isDefined from '../is/isDefined';
import isPromise from '../is/isPromise';

/**
 * A default middleware class implementation that serializes the contents of the state object to the underlying store 
 */
export class StateMiddleware {
    /**
     * Initialises the state middleware with the key of both the store data and the app state
     * to use for serialization
     * 
     * @param {String} stateKey  The key of the object within the top level redux state to serialize
     * @param {String} storeKey  The key to store this state under in the underlying app store
     */
    constructor(stateKey, storeKey) {
        this.stateKey = stateKey;
        this.storeKey = storeKey;
    }

    /**
     * Called when a redux state change has occurred.  This method is used to serialize the current state
     * into the underlying store
     * 
     * @param {Store} appStore The app store to serialize the data to
     * @param {Object} state The state to serialize
     */
    stateChanged(appStore, state) {
        appStore.set(this.storeKey, state[this.stateKey]);
    }

    /**
     * This method is called when the application starts up, and is used to rehydrate the redux state
     * object with data held within the store.
     * 
     * @param {Store} appStore The app store to deserialize state from
     * @param {Object} state The redux state to initialise
     */
    initialiseState(appStore, state) {
        state[this.stateKey] = appStore.get(this.storeKey) || {};
    }
}

/**
 * A builder used for creating state default middleware where redux data is serialized without further processing.
 * 
 * @param {String} stateKey  The key of the object within the top level redux state to serialize
 * @param {String} storeKey  The key to store this state under in the underlying app store
 */
export function buildDefaultMiddleware(stateKey, storeKey) {
    return new StateMiddleware(stateKey, storeKey);
}

/**
 * A state middleware class implementation using function delegates to provide the implementation of the lifecycle
 * methods
 */
class StateMiddlewareDelegate {
    /**
     * Initialises the middleware with the function delegates to proxy calls to
     */
    constructor(stateChangedHandler, initialiseStateHandler) {
        this.stateChangedHandler = stateChangedHandler;
        this.initialiseStateHandler = initialiseStateHandler;
    }

    /**
     * This method is called when the application starts up, and is used to rehydrate the redux state
     * object by calling the initialisation handler provided in the constructor
     * 
     * @param {Store} appStore The app store to deserialize state from
     * @param {Object} state The redux state to initialise
     */
    initialiseState(appStore, state) {
        return this.initialiseStateHandler(appStore, state);

    }

    /**
     * Called when a redux state change has occurred.  This method delegates the deserialization of state
     * to the function provided in the constructor to the class
     * 
     * @param {Store} appStore The app store to serialize the data to
     * @param {Object} state The state to serialize
     */
    stateChanged(appStore, state) {
        return this.stateChangedHandler(appStore, state);
    }
}

/**
 * A builder used for creating state middleware that delegates the lifecycle methods to the handlers
 * provided
 * 
 * @param {Function} onStateChanged A function to call when the redux state changes
 * @param {Function} onInitialiseState A function to call when the redux state is being initialised
 */
export function buildMiddleware(onStateChanged, onInitialiseState) {
    return new StateMiddlewareDelegate(onStateChanged, onInitialiseState);
}

/**
 * The state manager is responsible for managing the delegation of state initialisation and serialization
 * to and from an app store by invoking a series of configured middleware components
 */
export default class StateManager {

    middleware = [];

    /**
     * Adds the provided middleware components to the state manager for inclusion in the serialization
     * process.
     * 
     * @param {Array<StateMiddleware>} middleware The middleware components to add
     */
    applyMiddleware(middleware) {
        if (middleware && middleware.length) {
            const validMiddleware = middleware.filter(m => isDefined(m.initialiseState) && isDefined(m.stateChanged));

            if (validMiddleware.length !== middleware.length) {
                console.warn('Invalid middleware provided, must have both an "initialiseState" and "stateChanged" function defined');
            }

            this.middleware = this.middleware.concat(validMiddleware);
        }
        return this;
    }

    async asyncOp(state, op) {
        const promises = [Promise.resolve()];

        for (let middleware of this.middleware) {
            const result = op(middleware, appStore, state);
            if (isPromise(result)) {
                promises.push(result);
            }
        }

        try {
            await Promise.all(promises);
        } catch (e) {
            console.error(e);
        }
        
        return state;
    }

    /**
     * Called when the store state changes.
     */
    async onStateChanged(state) {        
        return await this.asyncOp(state, async (middleware, store, state) => await middleware.stateChanged(appStore, state));
    }

    /**
     * Called when the initial state ofthe store should be generated
     */
    async initialiseState() {
        const state = {};

        await this.asyncOp(state, (middleware, store, state) => middleware.initialiseState(appStore, state));

        return state;
    }
}
